import { DeviceOperationalState } from '@/__generated__/graphql';
import ClientHiddenChip from '@components/ClientHiddenChip';
import TruncatedTypography from '@components/TruncatedTypography';
import DeviceTooltip from '@components/tooltips/DeviceTooltip';
import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Box, Chip, ListItem, Popover, Typography } from '@mui/material';
import { useState, type FC, type MouseEvent } from 'react';
import type { UserDeviceAccessEntry } from './graphql/queries/getAllDeviceAccessForUser';

type CollectionRef = UserDeviceAccessEntry['collections'][number];

interface Props {
  style?: React.CSSProperties;
  lastItem?: boolean;
  deviceId: string;
  deviceName: string;
  deviceOrganization?: {
    id: number;
    name: string;
  } | null;
  deviceIrrigationName?: string;
  direct: boolean;
  collections: CollectionRef[];
  /** AMARU-597: false → acceso concedido pero aún no visible para el cliente (vista admin). */
  visibleToUser?: boolean;
  operationalState?: DeviceOperationalState | null;
}

const AllAccessListItem: FC<Props> = ({
  lastItem,
  style,
  deviceId,
  deviceName,
  deviceOrganization,
  deviceIrrigationName,
  direct,
  collections,
  visibleToUser = true,
  operationalState,
}) => {
  const { _ } = useLingui();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const onCollectionsChipClick = (event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);

  return (
    <ListItem
      disablePadding
      style={style}
      sx={{
        borderBottom: lastItem ? null : '1px solid',
        borderColor: 'divider',
        px: 2,
      }}
    >
      <DeviceTooltip deviceId={deviceId} placement="right-end">
        <Box
          display="flex"
          alignItems="center"
          width="100%"
          height="100%"
          gap={2}
          sx={{ opacity: visibleToUser ? 1 : 0.5 }}
        >
          <Box width="calc(100% - 220px)">
            <Box display="flex" alignItems="center" gap={2}>
              <TruncatedTypography fontSize={14} maxWidth="45%">
                {deviceName}
              </TruncatedTypography>
              {deviceOrganization ? (
                <TruncatedTypography fontSize={12} maxWidth="40%" color="text.secondary">
                  {deviceOrganization.name}
                </TruncatedTypography>
              ) : (
                <Typography alignSelf="center" fontSize="small" color="text.secondary" fontStyle="italic">
                  <Trans>Sin organización</Trans>
                </Typography>
              )}
            </Box>
            <Box display="flex" alignItems="center" gap={2}>
              <TruncatedTypography maxWidth="30%" fontSize={12} color="text.secondary">
                ID: {deviceId}
              </TruncatedTypography>
              <TruncatedTypography maxWidth="40%" fontSize={12} color="text.secondary">
                ICN: {deviceIrrigationName}
              </TruncatedTypography>
            </Box>
          </Box>
          <Box display="flex" flex={1} justifyContent="right" alignItems="center" gap={1}>
            {!visibleToUser && <ClientHiddenChip operationalState={operationalState} />}
            {direct && <Chip label={_(msg`Directo`)} size="small" color="info" variant="outlined" />}
            {collections.length > 0 && (
              <>
                <Chip
                  label={_(msg`Vía colección (${collections.length})`)}
                  size="small"
                  variant="outlined"
                  onClick={onCollectionsChipClick}
                />
                <Popover
                  open={Boolean(anchorEl)}
                  anchorEl={anchorEl}
                  onClose={() => setAnchorEl(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                >
                  <Box p={1} minWidth={200}>
                    {collections.map((collection) => (
                      <Typography key={collection.id} variant="body2">
                        {collection.name} ({collection.type})
                      </Typography>
                    ))}
                  </Box>
                </Popover>
              </>
            )}
          </Box>
        </Box>
      </DeviceTooltip>
    </ListItem>
  );
};

export default AllAccessListItem;
